<template>
  <div class="detail-group">
    <div class="secondary-details">
      <ul class="detail-list">
        <li>
          <span class="detail-title">Meal Type:</span>
          <span class="detail-value">{{ metaData.mealType }}</span>
        </li>
        <li>
          <span class="detail-title">Dietary Type:</span>
          <span class="detail-value">{{ metaData.dietaryType }}</span>
        </li>
        <li v-if="metaData.prepTime !== ''">
          <span class="detail-title">Prep Time:</span>
          <span class="detail-value">{{ formatTime(metaData.prepTime as number) }}</span>
        </li>
        <li v-if="metaData.cookTime !== ''">
          <span class="detail-title">Cook Time:</span>
          <span class="detail-value">{{ formatTime(metaData.cookTime as number) }}</span>
        </li>
        <li v-if="metaData.servingSize !== ''">
          <span class="detail-title">Servings:</span>
          <span class="detail-value">{{ metaData.servingSize }}</span>
        </li>

        <!-- only show nutrition if metadata exists -->
        <li
          v-if="
            !isDietaryMethodMindful &&
            (metaData.calories || metaData.protein || metaData.fat || metaData.carbs)
          "
        >
          <recipe-macros tag-is="li">
            <div class="nutrition-facts">
              <h1>Nutrition Facts</h1>
              <h2>Amount per Serving</h2>
              <div v-if="metaData.calories" class="fact">
                <span class="nf-title">Calories:</span>
                <span class="nf-value">{{ metaData.calories }}</span>
              </div>
              <div v-if="metaData.carbs" class="fact">
                <span class="nf-title">Total Carbohydrate:</span>
                <span class="nf-value">{{ metaData.carbs }} g</span>
              </div>
              <div v-if="metaData.protein" class="fact">
                <span class="nf-title">Protein:</span>
                <span class="nf-value">{{ metaData.protein }} g</span>
              </div>
              <div v-if="metaData.fat" class="fact">
                <span class="nf-title">Total Fat:</span>
                <span class="nf-value">{{ metaData.fat }} g</span>
              </div>
            </div>
          </recipe-macros>
        </li>
        <li>
          <span class="detail-title">Membership</span>
          <span
            class="detail-value"
            :class="{
              '-pill -plus': isExclusive,
              '-pill -free': !isExclusive,
            }"
          >
            {{ isExclusive ? 'Plus' : 'Free' }}
          </span>
          <span v-if="isPlusFreeAccess" class="detail-value -pill -plus-free-access">
            Free Days
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useArticlesStore } from '@/stores/articles'
import { useEntityContextStore } from '@/stores/entity-context'
import RecipeMacros from '@/components/articles/recipe-macros.vue'
import type { ArticleMetaData } from '@/types/article-types'

/**
 * Component only shows article recipe meta data, but state / management is outsourced
 * to pinia (article module, which must be loaded as it is not in api.js).
 *
 * - can pass in props to set initial pinia state
 * - receives pinia state updates to meta data
 *
 */
export default defineComponent({
  name: 'ArticleMetaData',
  components: {
    RecipeMacros,
  },
  props: {
    mealType: {
      type: String,
      default: '',
    },
    dietaryType: {
      type: String,
      default: '',
    },
    prepTime: {
      type: Number,
      default: null,
    },
    cookTime: {
      type: Number,
      default: null,
    },
    servingSize: {
      type: Number,
      default: null,
    },
    calories: {
      type: Number,
      default: null,
    },
    fat: {
      type: Number,
      default: null,
    },
    carbs: {
      type: Number,
      default: null,
    },
    protein: {
      type: Number,
      default: null,
    },
    isExclusive: {
      type: Boolean,
      default: false,
    },
    isPlusFreeAccess: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {}
  },

  computed: {
    ...mapState(useUserAuthStore, ['features', 'settings']),
    ...mapState(useArticlesStore, ['metaData', 'mode', 'scaleLoading', 'scaleError']),
    ...mapState(useEntityContextStore, ['context']),

    isPlanContext() {
      if (
        this.context &&
        this.context.context === 'plan' &&
        this.context.planDayId &&
        this.context.planDayScheduleId
      ) {
        return true
      }

      return false
    },

    hasEnergySettings() {
      return !!(this.settings && this.settings.energy && this.settings.energy.dailyCalorieType)
    },

    isScaleReady() {
      return (
        !this.scaleError &&
        !this.scaleLoading &&
        this.isPlanContext &&
        this.hasEnergySettings &&
        this.hasEnergySettings
      )
    },

    isDietaryMethodMindful() {
      if (
        this.settings &&
        this.settings.energy &&
        this.settings.energy.dietaryMethod === 'mindful'
      ) {
        return true
      }

      return false
    },
  },

  created() {
    // if context is plan, set values to -- as they will load via XHR

    // set initial data into pinia and display
    const originalMetaData = {
      mealType: this.mealType,
      dietaryType: this.dietaryType,
      prepTime: this.prepTime,
      cookTime: this.cookTime,
      servingSize: this.servingSize,
      calories: this.calories,
      fat: this.fat,
      carbs: this.carbs,
      protein: this.protein,
    }

    // save to cache so meta data can be reverted
    this.updateCache({ originalMetaData })

    // if context is plan, assume an energy card will show which
    // will fire an XHR with scaled meta data... in that case
    // to original numbers showing and then bliping to scaled,
    // show --
    if (this.context && this.context.context === 'plan') {
      this.updateMetaData({
        mealType: this.mealType,
        dietaryType: this.dietaryType,
        prepTime: '--',
        cookTime: '--',
        servingSize: this.servingSize,
        calories: '--',
        fat: '--',
        carbs: '--',
        protein: '--',
      } as ArticleMetaData)
    } else {
      this.updateMetaData(originalMetaData)
    }
  },

  methods: {
    ...mapActions(useArticlesStore, [
      'scaleRecipe',
      'resetRecipe',
      'updateMetaData',
      'updateCache',
    ]),

    /**
     * Tell pinia to revert the recipe data, but can be restored via cache
     */
    viewOriginalRecipe() {
      this.resetRecipe()
    },

    /**
     * Set pinia scaled recipe data from cache
     */
    viewScaledRecipe() {
      this.scaleRecipe()
    },

    /**
     * Convert hours to minutes if needed
     */
    formatTime(minutes: number) {
      if (minutes > 60) {
        const hours = Math.floor(minutes / 60)
        const hourStr = `hr${hours > 1 ? 's' : ''}`
        const remainderMinutes = minutes % 60

        return `${hours} ${hourStr} ${remainderMinutes ? `${remainderMinutes} min` : ''}`
      }

      return `${minutes} min`
    },
  },
})
</script>
